/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Typography,
  TextField,
  Card,
  Skeleton,
  Alert,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useTranslation } from "react-i18next";

const LineChartComponent = ({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  dataType = "ptw",
  isModalView = false,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // Local state for date inputs to prevent immediate API calls
  const [localFromDate, setLocalFromDate] = useState(fromDate);
  const [localToDate, setLocalToDate] = useState(toDate);

  // Update local state when parent props change
  useEffect(() => {
    setLocalFromDate(fromDate);
    setLocalToDate(toDate);
  }, [fromDate, toDate]);

  const endpoint = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const isoFrom = new Date(fromDate).toISOString();
    const isoTo = new Date(toDate).toISOString();
    const basePath =
      dataType === "ptw"
        ? "/api/v1/stats/work-permits/count/per-day"
        : "/api/v1/stats/observations/count/per-day";
    return `${basePath}?from=${isoFrom}&to=${isoTo}`;
  }, [fromDate, toDate, dataType]);

  const { data, isLoading, error } = useAuthenticatedQuery(
    [`${dataType}perday`, fromDate, toDate],
    endpoint || undefined,
    { enabled: Boolean(endpoint) }
  );

  const chartData = useMemo(() => {
    return (
      data?.map((item) => ({
        name: new Date(item.date).toLocaleDateString(i18n.language, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        count: item.count ?? 0,
      })) || []
    );
  }, [data, i18n.language]);

  // Calculate data summary for modal view
  const dataSummary = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const counts = chartData.map((d) => d.count);
    const total = counts.reduce((sum, count) => sum + count, 0);
    const max = Math.max(...counts);
    const min = Math.min(...counts);
    const avg = total / counts.length;

    return { total, max, min, avg: avg.toFixed(1) };
  }, [chartData]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: isModalView ? 3 : 0,
          bgcolor: theme.palette.dashboard.background.card,
        }}
      >
        {isModalView && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: theme.palette.dashboard.background.elevated,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width={150}
                height={56}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width={150}
                height={56}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Card>
        )}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={isModalView ? 400 : 200}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: isModalView ? 3 : 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t("lineChart.errorText")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("lineChart.errorSupportText")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Check if we have data to determine what to render in chart area
  const hasData = chartData && chartData.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.dashboard.background.card,
      }}
    >
      {/* Date Controls and Summary for Modal View */}
      {isModalView && (
        <Card
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: theme.palette.dashboard.background.subtle,
            boxShadow: theme.palette.dashboard.shadow.card,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.dashboard.text.primary,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CalendarIcon sx={{ fontSize: 20 }} />
            {t("lineChart.dateRangeTitle")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              label={t("lineChart.fromDateLabel")}
              type="datetime-local"
              value={localFromDate}
              onChange={(e) => setLocalFromDate(e.target.value)}
              onBlur={(e) => setFromDate(e.target.value)}
              size="small"
              slotProps={{
                htmlInput: {
                  max: localToDate || undefined,
                },
                input: {
                  sx: {
                    "& input[type='datetime-local']": {
                      "&::-webkit-calendar-picker-indicator": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                      "&::-webkit-datetime-edit": {
                        padding: "8px",
                      },
                    },
                  },
                },
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.dashboard.background.elevated,
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                  "& input[type='datetime-local']": {
                    "&::-webkit-calendar-picker-indicator": {
                      opacity: 1,
                      cursor: "pointer",
                    },
                    "&::-webkit-datetime-edit-fields-wrapper": {
                      padding: "4px 8px",
                    },
                  },
                },
              }}
            />
            <TextField
              label={t("lineChart.toDateLabel")}
              type="datetime-local"
              value={localToDate}
              onChange={(e) => setLocalToDate(e.target.value)}
              onBlur={(e) => setToDate(e.target.value)}
              size="small"
              slotProps={{
                htmlInput: {
                  min: localFromDate || undefined,
                },
                input: {
                  sx: {
                    "& input[type='datetime-local']": {
                      "&::-webkit-calendar-picker-indicator": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                      "&::-webkit-datetime-edit": {
                        padding: "8px",
                      },
                    },
                  },
                },
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.dashboard.background.elevated,
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                  "& input[type='datetime-local']": {
                    "&::-webkit-calendar-picker-indicator": {
                      opacity: 1,
                      cursor: "pointer",
                    },
                    "&::-webkit-datetime-edit-fields-wrapper": {
                      padding: "4px 8px",
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Data Summary - Only visible in modal view when there's data */}
          {hasData && dataSummary && (
            <Box
              sx={{
                mt: 2,
                position: "relative",
                zIndex: 3,
                minHeight: 60,
                mb: 2,
                bgcolor: theme.palette.dashboard.background.subtle,
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  color: theme.palette.dashboard.text.primary,
                  fontWeight: 600,
                  fontSize: { xs: 12, sm: 14 },
                }}
              >
                {t("lineChart.summaryTitle")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 0.5, sm: 1 },
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", sm: "flex-start" },
                  position: "relative",
                  zIndex: 3,
                }}
              >
                <Chip
                  label={`${t("lineChart.totalLabel")}: ${dataSummary.total}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: { xs: 10, sm: 12 },
                    height: { xs: 24, sm: 32 },
                    "& .MuiChip-label": {
                      px: { xs: 1, sm: 1.5 },
                    },
                  }}
                />
                <Chip
                  label={`${t("lineChart.avgLabel")}: ${dataSummary.avg}`}
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: { xs: 10, sm: 12 },
                    height: { xs: 24, sm: 32 },
                    "& .MuiChip-label": {
                      px: { xs: 1, sm: 1.5 },
                    },
                  }}
                />
                <Chip
                  label={`${t("lineChart.maxLabel")}: ${dataSummary.max}`}
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: { xs: 10, sm: 12 },
                    height: { xs: 24, sm: 32 },
                    "& .MuiChip-label": {
                      px: { xs: 1, sm: 1.5 },
                    },
                  }}
                />
                <Chip
                  label={`${t("lineChart.minLabel")}: ${dataSummary.min}`}
                  color="warning"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: { xs: 10, sm: 12 },
                    height: { xs: 24, sm: 32 },
                    "& .MuiChip-label": {
                      px: { xs: 1, sm: 1.5 },
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </Card>
      )}

      {/* Chart Container */}
      <Box
        sx={{
          height: isModalView ? 400 : 200,
          p: 1,
          mt: isModalView ? 0 : 1,
        }}
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
                opacity={0.3}
              />
              <XAxis
                dataKey="name"
                tick={{
                  fontSize: 12,
                  fill: theme.palette.dashboard.text.secondary,
                }}
                stroke={theme.palette.dashboard.text.secondary}
              />
              <YAxis
                tick={{
                  fontSize: 12,
                  fill: theme.palette.dashboard.text.secondary,
                }}
                stroke={theme.palette.dashboard.text.secondary}
              />
              {isModalView && (
                <Tooltip
                  contentStyle={{
                    backgroundColor:
                      theme.palette.dashboard.background.elevated,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    boxShadow: theme.palette.dashboard.shadow.card,
                    color: theme.palette.dashboard.text.primary,
                  }}
                  labelStyle={{
                    color: theme.palette.dashboard.text.primary,
                    fontWeight: 600,
                  }}
                />
              )}
              {isModalView && (
                <Legend
                  wrapperStyle={{
                    color: theme.palette.dashboard.text.secondary,
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="count"
                stroke={theme.palette.dashboard.chart.primary}
                strokeWidth={3}
                dot={{
                  fill: theme.palette.dashboard.chart.primary,
                  strokeWidth: 2,
                  r: 4,
                }}
                activeDot={{
                  r: 6,
                  fill: theme.palette.dashboard.chart.accent,
                  stroke: theme.palette.dashboard.background.card,
                  strokeWidth: 2,
                }}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              flexDirection: "column",
              gap: 2,
              bgcolor: theme.palette.dashboard.background.subtle,
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Typography
              color={theme.palette.dashboard.text.secondary}
              variant="body1"
              textAlign="center"
              sx={{ fontWeight: 500 }}
            >
              {t("lineChart.noDataText")}
            </Typography>
            {isModalView && (
              <Typography
                color={theme.palette.dashboard.text.secondary}
                variant="body2"
                textAlign="center"
                sx={{ opacity: 0.7 }}
              >
                {t("lineChart.noDataHint")}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LineChartComponent;
